interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

interface Job {
  id: string;
  networkId?: string;
  networkName?: string;
  source: string;
  cloneUrl?: string;
  commitSHA?: string;
  branch?: string;
  triggerType?: string;
  status: string;
  priority?: number;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
  validations?: Validation[];
}

interface Validation {
  id: string;
  jobId: string;
  type: string; // 'analysis', 'simulation', etc.
  status: string;
  result?: string;
  results?: {
    id: string;
    validationId: string;
    jobId: string;
    type: string;
    status: string;
    result?: string;
    summary: unknown;
    details: unknown;
    invariants: unknown;
    logs: unknown;
    errorMessage?: string;
    startedAt?: string;
    completedAt?: string;
  };
  logs: string | null;
  createdAt?: string;
  startedAt: string;
  completedAt: string | null;
  repository?: {
    id: string;
    name: string;
    fullName: string;
  };
}

interface User {
  id: string;
  email: string;
  name: string | null;
  image: string | null;
  plan: string;
  role: 'user' | 'admin' | 'superadmin';
  createdAt: string;
  agreedToTermsAt?: string;
  agreedToTermsVersion?: string;
}

interface Connection {
  id: string;
  provider: string;
  providerUserId: string;
  login: string;
  scopes: string;
  connectedAt: string;
}

// Import Network types
import type {
  Network,
  CreateNetworkRequest,
  UpdateNetworkRequest,
  DirectValidationRequest,
} from '../types/network';

class ApiClient {
  private baseUrl: string;
  private accessToken: string | null = null;
  private unauthorizedHandler: (() => void) | null = null;
  private isNotifyingUnauthorized = false;
  private refreshPromise: Promise<string | null> | null = null;

  constructor(baseUrl?: string) {
    // In production, use same origin since API is on same domain under /api
    // In development, use the env variable or localhost
    if (baseUrl) {
      this.baseUrl = baseUrl;
    } else if (typeof window !== 'undefined') {
      // Browser environment - use same origin for API calls
      this.baseUrl = window.location.origin;
    } else {
      // Server/build environment - use env variable
      this.baseUrl = import.meta.env?.VITE_APP_API_URL || 'http://localhost:8081';
    }
  }

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  setUnauthorizedHandler(handler: (() => void) | null) {
    this.unauthorizedHandler = handler;
  }

  private shouldNotifyUnauthorized(path: string): boolean {
    return !path.startsWith('/auth/');
  }

  private notifyUnauthorized() {
    if (!this.unauthorizedHandler || this.isNotifyingUnauthorized) {
      return;
    }

    this.isNotifyingUnauthorized = true;
    try {
      this.unauthorizedHandler();
    } finally {
      this.isNotifyingUnauthorized = false;
    }
  }

  private async tryRefreshAccessToken(): Promise<string | null> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      if (typeof window === 'undefined') {
        return null;
      }

      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        return null;
      }

      const refreshUrl = `${this.baseUrl}/api/v1/auth/refresh`;
      const response = await fetch(refreshUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
        credentials: 'include',
      });

      if (!response.ok) {
        return null;
      }

      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        return null;
      }

      const text = await response.text();
      if (!text) {
        return null;
      }

      const payload = JSON.parse(text) as { access_token?: string };
      const nextToken = payload.access_token;
      if (!nextToken) {
        return null;
      }

      this.setAccessToken(nextToken);
      localStorage.setItem('access_token', nextToken);
      return nextToken;
    })().catch(() => null).finally(() => {
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  }

  private async request<T = unknown>(
    path: string, 
    options: RequestInit = {},
    allowRetryOnUnauthorized = true
  ): Promise<ApiResponse<T>> {
    // API is now under /api on the same domain
    const url = `${this.baseUrl}/api/v1${path}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.accessToken) {
      headers.Authorization = `Bearer ${this.accessToken}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include', // Include cookies for auth
      });

      // Handle 204 No Content and other empty responses
      let data: unknown = null;
      const contentType = response.headers.get('content-type');
      if (response.status !== 204 && contentType?.includes('application/json')) {
        const text = await response.text();
        data = text ? JSON.parse(text) : null;
      }

      if (!response.ok) {
        if (response.status === 401 && this.shouldNotifyUnauthorized(path)) {
          if (allowRetryOnUnauthorized) {
            const refreshedToken = await this.tryRefreshAccessToken();
            if (refreshedToken) {
              return this.request<T>(path, options, false);
            }
          }
          this.notifyUnauthorized();
        }
        const errorData = data as { error?: string; message?: string } | null;
        const serverMessage = errorData?.error || errorData?.message || '';
        const errorMessage = serverMessage
          ? `${serverMessage} (HTTP ${response.status})`
          : `HTTP ${response.status}`;
        return {
          success: false,
          error: errorMessage,
        };
      }

      return {
        success: true,
        data: data as T,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  // Auth endpoints
  async login(email: string, password: string) {
    return this.request<{ access_token: string; refresh_token: string; user: User }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async refreshToken(refreshToken: string) {
    return this.request<{ access_token: string }>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  }

  async logout() {
    return this.request('/auth/logout', {
      method: 'POST',
    });
  }

  async getProfile() {
    return this.request<User>('/auth/profile');
  }

  // User endpoints
  async updateProfile(data: { name?: string; email?: string; image?: string }) {
    return this.request<User>('/user/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getUserStats() {
    return this.request<{
      total_repositories: number;
      total_validations: number;
      validations_today: number;
      daily_limit: number;
      plan: string;
    }>('/user/stats');
  }

  async deleteAccount() {
    return this.request('/user/account', {
      method: 'DELETE',
    });
  }

  // API Key endpoints
  async getApiKeys() {
    return this.request<Array<{
      id: string;
      name: string;
      key_prefix: string;
      permissions: string[];
      last_used_at: string | null;
      expires_at: string | null;
      created_at: string;
    }>>('/user/api-keys');
  }

  async createApiKey(data: {
    name: string;
    permissions?: string[];
    expires_in_days?: number;
  }) {
    return this.request<{
      id: string;
      name: string;
      key_prefix: string;
      permissions: string[];
      expires_at: string | null;
      created_at: string;
      key: string;
    }>('/user/api-keys', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async revokeApiKey(id: string) {
    return this.request(`/user/api-keys/${id}`, {
      method: 'DELETE',
    });
  }

  async getRepositories() {
    return this.request<{ repositories: unknown[] }>('/repositories');
  }

  // Connection endpoints
  async listConnections() {
    return this.request<{ connections: Connection[] }>('/connections');
  }

  async disconnectGitHub() {
    return this.request('/connections/github', {
      method: 'DELETE',
    });
  }

  getConnectGitHubUrl(): string {
    return `${this.baseUrl}/api/v1/connections/github`;
  }

  getGitHubAppInstallUrl(): string {
    const slug = import.meta.env.VITE_GITHUB_APP_SLUG || 'etherpilot-bot';
    return `https://github.com/apps/${slug}/installations/new`;
  }

  async updateApiKey(id: string, data: { permissions: string[] }) {
    return this.request(`/user/api-keys/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Network endpoints
  async getNetworks() {
    return this.request<{ networks: Network[]; count: number }>('/networks');
  }

  async listNetworks() {
    return this.getNetworks();
  }

  async getNetwork(id: string) {
    return this.request<{ network: Network }>(`/networks/${id}`);
  }

  async createNetwork(data: CreateNetworkRequest) {
    return this.request<{ network: Network }>('/networks', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateNetwork(id: string, data: UpdateNetworkRequest) {
    return this.request<{ network: Network }>(`/networks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteNetwork(id: string) {
    return this.request(`/networks/${id}`, {
      method: 'DELETE',
    });
  }

  async getNetworkJobs(id: string) {
    return this.request<{ jobs: Job[]; count: number }>(`/networks/${id}/jobs`);
  }

  async getNetworkValidations(id: string) {
    return this.request<{ validations: Validation[]; count: number }>(`/networks/${id}/validations`);
  }

  async triggerNetworkValidation(networkId: string, data: {
    validationTypes?: string[];
    branch?: string;
    configPath?: string;
  }) {
    return this.request<{
      message: string;
      job_id: string;
      validations: Validation[];
      status: string;
    }>(`/networks/${networkId}/validations`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Direct validation (CLI-style submissions)
  async createDirectValidation(data: DirectValidationRequest) {
    return this.request<{
      message: string;
      job_id: string;
      network_id: string;
      validations: Validation[];
    }>('/validations/direct', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async triggerValidation(networkId: string, data: {
    validationType?: string;
    branch?: string;
    configPath?: string;
  }) {
    return this.request<{
      message: string;
      job_id: string;
      validations: Validation[];
      status: string;
      quota?: {
        used: number;
        limit: number;
      };
    }>(`/networks/${networkId}/validations`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Discovery endpoints
  async getNetworkDiscoveryStatus(networkId: string) {
    return this.request<{
      status: string;
      result?: string;
      networkYaml?: string;
      invariantsYaml?: string;
      lastDiscoveryAt?: string;
      confidence?: number;
      pattern?: string;
    }>(`/networks/${networkId}/discovery`);
  }

  async inspectNetworkConfig(networkId: string, branch?: string) {
    return this.request<{
      hasConfig: boolean;
      networkYaml?: string;
      invariantsYaml?: string;
      bundleFiles?: Array<{
        path: string;
        content: string;
        htmlUrl?: string;
      }>;
      branch?: string;
      commitSha?: string;
      lastInspectedAt?: string;
    }>(`/networks/${networkId}/inspect${branch ? `?branch=${branch}` : ''}`);
  }

  async triggerNetworkDiscovery(networkId: string) {
    return this.request<{
      status: string;
      message: string;
    }>(`/networks/${networkId}/discovery`, {
      method: 'POST',
    });
  }

  async cancelNetworkDiscovery(networkId: string) {
    return this.request<void>(`/networks/${networkId}/discovery`, {
      method: 'DELETE',
    });
  }

  async pushNetworkDiscoveryToGitHub(networkId: string, data: {
    branch: string;
    createPR: boolean;
    commitMessage: string;
  }) {
    return this.request<{
      message: string;
      branch: string;
      pullRequestUrl?: string;
    }>(`/networks/${networkId}/discovery/push`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Job endpoints
  async getJobs() {
    return this.request<{ jobs: Job[]; count: number }>('/jobs');
  }

  async getJob(id: string) {
    return this.request<{ job: unknown }>(`/jobs/${id}`);
  }

  async getJobValidations(jobId: string) {
    return this.request<{ validations: Validation[] }>(`/jobs/${jobId}/validations`);
  }

  async triggerJob(repoId: string, data?: {
    validationType?: string;
    branch?: string;
    configPath?: string;
  }) {
    return this.triggerValidation(repoId, data || {});
  }

  // Validation endpoints
  async getValidation(id: string) {
    return this.request<{ validation: Validation }>(`/validations/${id}`);
  }

  async getValidationResults(id: string) {
    return this.request<{ results: unknown }>(`/validations/${id}/results`);
  }

  async getValidationLogs(id: string) {
    return this.request<{ logs: string[] }>(`/validations/${id}/logs`);
  }

  async cancelValidation(id: string) {
    return this.request<{ validation: Validation }>(`/validations/${id}/cancel`, {
      method: 'POST',
    });
  }

  // Progress endpoint for SSE
  getValidationProgressUrl(id: string): string {
    return `${this.baseUrl}/api/v1/validations/${id}/progress`;
  }

  // Agreement endpoints
  async getAgreement() {
    return this.request<{ version: string; title: string; text: string }>('/user/agreement');
  }

  async acceptAgreement(version: string) {
    return this.request<User>('/user/accept-agreement', {
      method: 'POST',
      body: JSON.stringify({ version }),
    });
  }

  // Health endpoints
  async getHealth() {
    return this.request('/health');
  }

  async getHealthz() {
    return this.request('/healthz');
  }

  async getReadyz() {
    return this.request('/readyz');
  }

  // Admin endpoints

  async getAdminStats() {
    return this.request('/admin/stats');
  }

  async listAdminUsers(params: { page?: number; pageSize?: number; plan?: string; role?: string; status?: string; search?: string } = {}) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.pageSize) query.set('pageSize', String(params.pageSize));
    if (params.plan) query.set('plan', params.plan);
    if (params.role) query.set('role', params.role);
    if (params.status) query.set('status', params.status);
    if (params.search) query.set('search', params.search);
    return this.request(`/admin/users?${query.toString()}`);
  }

  async getAdminUser(id: string) {
    return this.request(`/admin/users/${id}`);
  }

  async listUserNetworks(id: string) {
    return this.request(`/admin/users/${id}/networks`);
  }

  async updateUserRole(id: string, role: string) {
    return this.request(`/admin/users/${id}/role`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    });
  }

  async changeUserPlan(id: string, plan: string, reason: string) {
    return this.request(`/admin/users/${id}/plan`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan, reason }),
    });
  }

  async lockUser(id: string, reason: string) {
    return this.request(`/admin/users/${id}/lock`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
  }

  async unlockUser(id: string) {
    return this.request(`/admin/users/${id}/unlock`, {
      method: 'POST',
    });
  }

  async listSubscriptionHistory(params: { userId?: string; limit?: number; offset?: number } = {}) {
    const query = new URLSearchParams();
    if (params.userId) query.set('userId', params.userId);
    if (params.limit) query.set('limit', String(params.limit));
    if (params.offset) query.set('offset', String(params.offset));
    return this.request(`/admin/billing/history?${query.toString()}`);
  }

  async listInvoices(params: { customerId?: string; limit?: number } = {}) {
    const query = new URLSearchParams();
    if (params.customerId) query.set('customerId', params.customerId);
    if (params.limit) query.set('limit', String(params.limit));
    return this.request(`/admin/billing/invoices?${query.toString()}`);
  }

  async listBetaSignups(params: { status?: string; limit?: number; offset?: number } = {}) {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.limit) query.set('limit', String(params.limit));
    if (params.offset) query.set('offset', String(params.offset));
    return this.request(`/admin/beta-signups?${query.toString()}`);
  }

  async approveBetaSignup(id: string, notes?: string) {
    return this.request(`/admin/beta-signups/${id}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notes: notes || '' }),
    });
  }

  async declineBetaSignup(id: string, notes?: string) {
    return this.request(`/admin/beta-signups/${id}/decline`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notes: notes || '' }),
    });
  }

  async authorizeDevice(userCode: string) {
    return this.request('/auth/device/authorize', {
      method: 'POST',
      body: JSON.stringify({ user_code: userCode }),
    });
  }

  async listAuditLogs(params: { userId?: string; limit?: number; offset?: number } = {}) {
    const query = new URLSearchParams();
    if (params.userId) query.set('userId', params.userId);
    if (params.limit) query.set('limit', String(params.limit));
    if (params.offset) query.set('offset', String(params.offset));
    return this.request(`/admin/audit-logs?${query.toString()}`);
  }
}

// Create singleton instance
export const apiClient = new ApiClient();
export { ApiClient };
export type { ApiResponse, Job, Validation, User, Connection };
export type { Network, CreateNetworkRequest, UpdateNetworkRequest, DirectValidationRequest } from '../types/network';
